import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from '../utils/safeToast';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Home,
  User,
  Clock,
  AlertTriangle,
  CheckCircle,
  Loader2,
  History,
  Camera,
  Upload,
  X,
  Mail,
  Send,
  Image as ImageIcon,
  ExternalLink,
  Tag,
  RefreshCw
} from 'lucide-react';

const API = "/api";
const BACKEND_URL = "";

const TaskDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [task, setTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [emailViewDialogOpen, setEmailViewDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [syncingEmails, setSyncingEmails] = useState(false);
  const [emailData, setEmailData] = useState({
    recipient_email: '',
    recipient_name: '',
    subject_suffix: '',
    body: '',
    include_images: true,
    selectedTags: [],
    freeText: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [ccExternal, setCcExternal] = useState(false); // Checkbox: Kopie an externen Empfänger

  // E-Mail Tags - werden dynamisch aus der Datenbank geladen
  const [categoryTags, setCategoryTags] = useState([]);
  const [selectedTextTags, setSelectedTextTags] = useState([]);
  const [generatingText, setGeneratingText] = useState(false);
  const [rawNotes, setRawNotes] = useState(''); // Stichsätze für Textgenerierung
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);
  
  // Mapping von Aufgaben-Kategorie zu Tag-Kategorie
  const CATEGORY_MAPPING = {
    eigentuemer: 'eigentuemer',
    reinigung: 'reinigung',
    reparatur: 'handwerker'  // Reparatur-Aufgaben nutzen Handwerker-Tags
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // Lade Tags für die aktuelle Kategorie
  const loadCategoryTags = async (category) => {
    const tagCategory = CATEGORY_MAPPING[category] || category;
    try {
      const res = await axios.get(`${API}/tags`, { params: { category: tagCategory } });
      setCategoryTags(res.data.filter(t => t.is_active !== false));
    } catch (error) {
      console.error('Fehler beim Laden der Kategorie-Tags:', error);
      setCategoryTags([]);
    }
  };

  const fetchData = async () => {
    try {
      const [taskRes, usersRes, tagsRes] = await Promise.all([
        axios.get(`${API}/tasks/${id}`),
        axios.get(`${API}/users`),
        axios.get(`${API}/tags`)
      ]);
      setTask(taskRes.data);
      setUsers(usersRes.data);
      setAvailableTags(tagsRes.data);
      setFormData({ ...taskRes.data, tags: taskRes.data.tags || [] });
      
      // Lade Tags für die Kategorie der Aufgabe
      await loadCategoryTags(taskRes.data.category);
      
      // Pre-fill email data with ticket-based subject
      const ticketId = taskRes.data.ticket_id || '';
      const propertyName = taskRes.data.property_name || 'Objekt';
      setEmailData(prev => ({
        ...prev,
        subject_suffix: taskRes.data.title,
        body: generatePlainTextBody(taskRes.data, [], ''),
        formatted_subject: ticketId ? `[${ticketId}] ${propertyName} – ${taskRes.data.title}` : `Aufgabe: ${taskRes.data.title}`,
        selectedTags: [],
        freeText: ''
      }));
      
      // Fetch recipients based on category
      await fetchRecipients(taskRes.data.category, taskRes.data.property_id);
    } catch (error) {
      toast.error('Fehler beim Laden der Aufgabe');
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async (category, propertyId) => {
    setLoadingRecipients(true);
    try {
      const response = await axios.get(`${API}/contacts/recipients/${category}`, {
        params: { property_id: propertyId }
      });
      setRecipients(response.data.recipients || []);
      
      // Auto-select default recipient (marked with is_default) or first if none marked
      if (response.data.recipients && response.data.recipients.length > 0) {
        const defaultRecipient = response.data.recipients.find(r => r.is_default) || response.data.recipients[0];
        setEmailData(prev => ({
          ...prev,
          recipient_email: defaultRecipient.email,
          recipient_name: defaultRecipient.contact_person || defaultRecipient.name,
          recipient_salutation: defaultRecipient.salutation || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
      setRecipients([]);
    } finally {
      setLoadingRecipients(false);
    }
  };

  // E-Mail Body als Plaintext generieren (für Vorschau/Bearbeitung)
  const generatePlainTextBody = (taskData, selectedTagIds = [], freeText = '') => {
    const categoryLabels = {
      eigentuemer: 'Eigentümer',
      reinigung: 'Reinigung',
      reparatur: 'Reparatur'
    };
    
    const priorityLabels = {
      normal: 'Normal',
      hoch: 'Hoch'
    };

    // Tags Text
    let tagsText = '';
    if (selectedTagIds.length > 0) {
      const tagNames = selectedTagIds
        .map(tagId => categoryTags.find(t => t.id === tagId))
        .filter(Boolean)
        .map(tag => `• ${tag.name}: ${tag.default_text || ''}`);
      
      if (tagNames.length > 0) {
        tagsText = '\nHINWEISE:\n' + tagNames.join('\n') + '\n';
      }
    }

    // Freitext
    let freeTextSection = '';
    if (freeText && freeText.trim()) {
      freeTextSection = '\nZUSÄTZLICHE INFORMATIONEN:\n' + freeText.trim() + '\n';
    }

    // Prioritäts-Warnung
    let priorityWarning = '';
    if (taskData.priority === 'hoch') {
      priorityWarning = '\n⚠️ HOHE PRIORITÄT – Diese Aufgabe erfordert zeitnahe Bearbeitung.\n';
    }

    // Anrede basierend auf Kategorie
    const greetings = {
      eigentuemer: 'Sehr geehrte/r Eigentümer/in',
      reinigung: 'Liebes Reinigungsteam',
      reparatur: 'Sehr geehrte Damen und Herren'
    };

    return `${greetings[taskData.category] || 'Sehr geehrte Damen und Herren'},

wir informieren Sie über folgende Aufgabe:
${priorityWarning}
───────────────────────────────────
OBJEKT:     ${taskData.property_name || '-'}
AUFGABE:    ${taskData.title}
KATEGORIE:  ${categoryLabels[taskData.category] || taskData.category}
PRIORITÄT:  ${priorityLabels[taskData.priority] || taskData.priority}${taskData.reporter_name ? `\nGEMELDET VON: ${taskData.reporter_name}` : ''}
───────────────────────────────────

BESCHREIBUNG:
${taskData.description}
${tagsText}${freeTextSection}
${taskData.images && taskData.images.length > 0 ? `\n📎 ${taskData.images.length} Foto(s) als Anhang beigefügt\n` : ''}
───────────────────────────────────
Diese E-Mail wurde automatisch generiert.
${taskData.ticket_id ? `Bitte behalten Sie [${taskData.ticket_id}] im Betreff bei Ihrer Antwort.` : ''}
GB Sylt – Objekt- & Aufgabenmanagement`.trim();
  };

  // E-Mail Body als HTML generieren (nur für den tatsächlichen Versand)
  const generateHtmlBody = (taskData, plainTextBody) => {
    const categoryLabels = {
      eigentuemer: 'Eigentümer',
      reinigung: 'Reinigung',
      reparatur: 'Reparatur'
    };
    
    const priorityLabels = {
      normal: 'Normal',
      hoch: 'Hoch'
    };

    let priorityWarning = '';
    if (taskData.priority === 'hoch') {
      priorityWarning = `
<div style="background: #fef2f2; border: 1px solid #fecaca; padding: 12px; border-radius: 8px; margin-bottom: 20px;">
  <strong style="color: #dc2626;">⚠️ Hohe Priorität</strong> – Diese Aufgabe erfordert zeitnahe Bearbeitung.
</div>`;
    }

    // Konvertiere Plaintext zu HTML-formatiert
    const descriptionHtml = plainTextBody
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
    
    return `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="background: #0E4C92; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
    <h1 style="margin: 0; font-size: 18px;">GB Objekt- & Aufgabenmanagement</h1>
    ${taskData.ticket_id ? `<p style="margin: 5px 0 0 0; opacity: 0.9; font-size: 14px;">Ticket: ${taskData.ticket_id}</p>` : ''}
  </div>
  
  <div style="padding: 25px; background: #ffffff; border: 1px solid #e2e8f0; border-top: none;">
    ${priorityWarning}
    
    <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
      <tr>
        <td style="padding: 10px; background: #f8fafc; border: 1px solid #e2e8f0; width: 140px;"><strong>Objekt</strong></td>
        <td style="padding: 10px; border: 1px solid #e2e8f0;">${taskData.property_name || '-'}</td>
      </tr>
      <tr>
        <td style="padding: 10px; background: #f8fafc; border: 1px solid #e2e8f0;"><strong>Aufgabe</strong></td>
        <td style="padding: 10px; border: 1px solid #e2e8f0;">${taskData.title}</td>
      </tr>
      <tr>
        <td style="padding: 10px; background: #f8fafc; border: 1px solid #e2e8f0;"><strong>Kategorie</strong></td>
        <td style="padding: 10px; border: 1px solid #e2e8f0;">${categoryLabels[taskData.category] || taskData.category}</td>
      </tr>
      <tr>
        <td style="padding: 10px; background: #f8fafc; border: 1px solid #e2e8f0;"><strong>Priorität</strong></td>
        <td style="padding: 10px; border: 1px solid #e2e8f0;">${priorityLabels[taskData.priority] || taskData.priority}</td>
      </tr>
    </table>
    
    <div style="background: #f8fafc; padding: 15px; border-radius: 8px; white-space: pre-wrap; font-family: Arial, sans-serif; font-size: 14px; line-height: 1.5;">
${plainTextBody}
    </div>
    
    ${taskData.images && taskData.images.length > 0 ? `
    <p style="margin-top: 20px; color: #64748b; font-size: 13px;">
      📎 ${taskData.images.length} Foto(s) als Anhang beigefügt
    </p>` : ''}
  </div>
  
  <div style="background: #f8fafc; padding: 15px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 8px 8px; font-size: 12px; color: #64748b;">
    <p style="margin: 0;">Diese E-Mail wurde automatisch generiert.</p>
    ${taskData.ticket_id ? `<p style="margin: 5px 0 0 0;">Bitte behalten Sie <strong>[${taskData.ticket_id}]</strong> im Betreff bei Ihrer Antwort.</p>` : ''}
    <p style="margin: 5px 0 0 0;">GB Sylt – Objekt- & Aufgabenmanagement</p>
  </div>
</div>`.trim();
  };

  const updateEmailBody = (newTags, newFreeText) => {
    if (task) {
      const body = generatePlainTextBody(task, newTags, newFreeText);
      setEmailData(prev => ({ ...prev, body, selectedTags: newTags, freeText: newFreeText }));
    }
  };

  const handleTagToggle = (tagId) => {
    const newTags = emailData.selectedTags.includes(tagId)
      ? emailData.selectedTags.filter(t => t !== tagId)
      : [...emailData.selectedTags, tagId];
    updateEmailBody(newTags, emailData.freeText);
  };

  const handleFreeTextChange = (text) => {
    setEmailData(prev => ({ ...prev, freeText: text }));
    // Debounce the body update
    clearTimeout(window.emailFreeTextTimeout);
    window.emailFreeTextTimeout = setTimeout(() => {
      updateEmailBody(emailData.selectedTags, text);
    }, 500);
  };

  // Toggle für Text-Generierungs-Tags im Bearbeitungs-Dialog
  const handleTextTagToggle = (tagId) => {
    setSelectedTextTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId) 
        : [...prev, tagId]
    );
  };

  // Stichsätze in vollständige Sätze umwandeln
  const transformNotesToSentences = (notes) => {
    if (!notes || !notes.trim()) return [];
    
    return notes
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        // Entferne führende Aufzählungszeichen
        let cleaned = line.replace(/^[-•*]\s*/, '').trim();
        
        // Ersten Buchstaben großschreiben
        if (cleaned.length > 0) {
          cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
        }
        
        // Punkt am Ende hinzufügen, falls nicht vorhanden
        if (cleaned.length > 0 && !cleaned.match(/[.!?]$/)) {
          cleaned += '.';
        }
        
        return cleaned;
      })
      .filter(line => line.length > 1);
  };

  // Regelbasierte Textgenerierung (ohne KI)
  const generateRuleBasedText = () => {
    const selectedRecipient = recipients.find(r => r.email === emailData.recipient_email);
    const recipientName = selectedRecipient?.contact_person || selectedRecipient?.name || '';
    const salutation = selectedRecipient?.salutation || '';
    
    // Anrede generieren
    let greeting = '';
    if (recipientName) {
      const lastName = recipientName.split(' ').pop() || recipientName;
      if (salutation === 'Frau' || salutation === 'Herr') {
        greeting = `Sehr geehrte${salutation === 'Frau' ? '' : 'r'} ${salutation} ${lastName},`;
      } else {
        greeting = `Moin ${recipientName},`;
      }
    } else {
      greeting = 'Sehr geehrte Damen und Herren,';
    }
    
    // Tag-Namen sammeln
    const selectedTagNames = selectedTextTags
      .map(tagId => categoryTags.find(t => t.id === tagId))
      .filter(Boolean)
      .map(tag => tag.name);
    
    // Notizen transformieren
    const sentences = transformNotesToSentences(rawNotes);
    
    // Titel generieren
    let title = '';
    if (selectedTagNames.length > 0) {
      title = selectedTagNames.slice(0, 3).join(', ');
      if (selectedTagNames.length > 3) {
        title += ' u.a.';
      }
    } else if (sentences.length > 0) {
      // Ersten Stichsatz als Titel verwenden
      title = sentences[0].replace(/\.$/, '');
      if (title.length > 50) {
        title = title.substring(0, 47) + '...';
      }
    } else {
      title = 'Hinweise zum Objekt';
    }
    
    // Text zusammenbauen
    let text = greeting + '\n\n';
    text += 'bei einer Begehung des Objekts wurden folgende Punkte festgestellt:\n\n';
    
    // Betroffene Bereiche (nur wenn Tags vorhanden)
    if (selectedTagNames.length > 0) {
      text += '**Betroffene Bereiche:**\n';
      selectedTagNames.forEach(tagName => {
        text += `• ${tagName}\n`;
      });
      text += '\n';
    }
    
    // Hinweise / Details (nur wenn Notizen vorhanden)
    if (sentences.length > 0) {
      text += '**Hinweise / Details:**\n';
      sentences.forEach(sentence => {
        text += `• ${sentence}\n`;
      });
      text += '\n';
    }
    
    text += 'Bitte geben Sie uns kurz Rückmeldung, wie wir hier weiter vorgehen sollen.\n\n';
    text += 'Vielen Dank und freundliche Grüße\n';
    text += 'GB Sylt GmbH';
    
    return { title, text };
  };

  // Prüfen ob Button aktiviert werden soll
  const canGenerateText = selectedTextTags.length > 0 || (rawNotes && rawNotes.trim().length > 0);

  // Text generieren Handler
  const handleGenerateText = async () => {
    if (!canGenerateText) {
      toast.error('Bitte wählen Sie mindestens einen Tag aus oder geben Sie Notizen ein');
      return;
    }
    
    // Warnung wenn bereits Text vorhanden
    if (formData.description && formData.description.trim().length > 0) {
      setConfirmOverwriteOpen(true);
      return;
    }
    
    executeTextGeneration();
  };

  const executeTextGeneration = () => {
    setGeneratingText(true);
    setConfirmOverwriteOpen(false);
    
    try {
      const { title, text } = generateRuleBasedText();
      
      setFormData(prev => ({
        ...prev,
        title: title,
        description: text
      }));
      
      const tagCount = selectedTextTags.length;
      const noteCount = transformNotesToSentences(rawNotes).length;
      
      let message = 'Text generiert';
      if (tagCount > 0 && noteCount > 0) {
        message = `Text aus ${tagCount} Tag(s) und ${noteCount} Notiz(en) generiert`;
      } else if (tagCount > 0) {
        message = `Text aus ${tagCount} Tag(s) generiert`;
      } else if (noteCount > 0) {
        message = `Text aus ${noteCount} Notiz(en) generiert`;
      }
      
      toast.success(message);
    } catch (error) {
      toast.error('Fehler bei der Textgenerierung');
    } finally {
      setGeneratingText(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(`${API}/tasks/${id}`, {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        priority: formData.priority,
        status: formData.status,
        assigned_to: formData.assigned_to || null,
        tags: formData.tags || []
      });
      toast.success('Aufgabe aktualisiert');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/tasks/${id}`, { status: newStatus });
      toast.success('Status aktualisiert');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/tasks/${id}`);
      toast.success('Aufgabe gelöscht');
      navigate('/tasks');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingImage(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        await axios.post(`${API}/tasks/${id}/images`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        toast.success(`${file.name} hochgeladen`);
      } catch (error) {
        toast.error(`Fehler beim Hochladen von ${file.name}`);
      }
    }
    
    setUploadingImage(false);
    fetchData();
    fileInputRef.current.value = '';
  };

  const handleDeleteImage = async (imageUrl) => {
    try {
      await axios.delete(`${API}/tasks/${id}/images?image_url=${encodeURIComponent(imageUrl)}`);
      toast.success('Bild gelöscht');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen des Bildes');
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSendingEmail(true);
    try {
      // Generate HTML body from plaintext for sending
      const htmlBody = generateHtmlBody(task, emailData.body);

      // Use MS Graph API endpoint
      // To: immer info@gb-sylt.de (Backend-Pflicht)
      // CC: recipient_email wenn ccExternal-Checkbox gesetzt
      const response = await axios.post(`${API}/tasks/${id}/send-email`, {
        recipient_email: ccExternal ? (emailData.recipient_email || null) : null,
        recipient_name: ccExternal ? (emailData.recipient_name || null) : null,
        subject_suffix: emailData.subject_suffix || task.title,
        body_html: htmlBody,
        include_images: emailData.include_images,
        cc_external: ccExternal,
      });

      toast.success('E-Mail erfolgreich versendet');
      if (response.data.ticket_id) {
        toast.info(`Ticket-ID: ${response.data.ticket_id}`, { duration: 5000 });
      }
      setEmailDialogOpen(false);
      setCcExternal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim E-Mail-Versand');
    } finally {
      setSendingEmail(false);
    }
  };

  // E-Mail Sync Funktion
  const handleSyncEmails = async () => {
    setSyncingEmails(true);
    try {
      const response = await axios.post(`${API}/email/sync`);
      const { assigned, unassigned, processed } = response.data;
      
      if (assigned > 0) {
        toast.success(`${assigned} neue E-Mail(s) zugeordnet`);
        // Aufgabe neu laden um den E-Mail-Thread zu aktualisieren
        fetchData();
      } else if (processed > 0) {
        toast.info(`${processed} E-Mail(s) verarbeitet, ${unassigned} nicht zugeordnet`);
      } else {
        toast.info('Keine neuen E-Mails gefunden');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim E-Mail-Sync');
    } finally {
      setSyncingEmails(false);
    }
  };

  // E-Mail öffnen und anzeigen
  const handleOpenEmail = (email) => {
    setSelectedEmail(email);
    setEmailViewDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      neu: 'bg-blue-100 text-blue-700',
      in_bearbeitung: 'bg-amber-100 text-amber-700',
      erledigt: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      neu: 'Neu',
      in_bearbeitung: 'In Bearbeitung',
      erledigt: 'Erledigt'
    };
    return <Badge className={`${styles[status]} border-0 text-sm px-3 py-1`}>{labels[status]}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'hoch') {
      return (
        <Badge className="bg-[#0E4C92] text-white border-0 font-bold">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Hohe Priorität
        </Badge>
      );
    }
    return <Badge className="bg-slate-100 text-slate-600 border-0">Normale Priorität</Badge>;
  };

  const getCategoryLabel = (category) => {
    const labels = { eigentuemer: 'Eigentümer', reinigung: 'Reinigung', reparatur: 'Reparatur' };
    return labels[category] || category;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E4C92]"></div>
      </div>
    );
  }

  if (!task) return null;

  return (
    <div className="space-y-6" data-testid="task-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/tasks')}
          className="p-2 mt-1"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            {task.ticket_id && (
              <Badge className="bg-[#0E4C92] text-white border-0 font-mono text-sm px-3 py-1" data-testid="ticket-id-badge">
                {task.ticket_id}
              </Badge>
            )}
            {getStatusBadge(task.status)}
            {getPriorityBadge(task.priority)}
            <Badge variant="outline">{getCategoryLabel(task.category)}</Badge>
            {task.email_status === 'versendet' && (
              <Badge className="bg-emerald-100 text-emerald-700 border-0">
                <Mail className="w-3 h-3 mr-1" />
                E-Mail gesendet
              </Badge>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            {task.title}
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setEmailDialogOpen(true)}
            className="rounded-lg border-[#0E4C92] text-[#0E4C92] hover:bg-[#0E4C92] hover:text-white"
            data-testid="send-email-btn"
          >
            <Mail className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">E-Mail</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => setEditDialogOpen(true)}
            className="rounded-lg"
            data-testid="edit-task-btn"
          >
            <Edit className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Bearbeiten</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => setDeleteDialogOpen(true)}
            className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="delete-task-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="bg-white border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Beschreibung</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 whitespace-pre-wrap">{task.description}</p>
            </CardContent>
          </Card>

          {/* Images */}
          <Card className="bg-white border border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-slate-600" />
                Bilder ({task.images?.length || 0})
              </CardTitle>
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="image-upload-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  data-testid="upload-image-btn"
                >
                  {uploadingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Camera className="w-4 h-4 mr-2" />
                  )}
                  Bild hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {task.images && task.images.length > 0 ? (
                <div className="image-gallery">
                  {task.images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={`${BACKEND_URL}${img}`}
                        alt={`Bild ${index + 1}`}
                        onClick={() => {
                          setSelectedImage(`${BACKEND_URL}${img}`);
                          setImageDialogOpen(true);
                        }}
                        className="cursor-pointer"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage(img);
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-image-${index}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Keine Bilder vorhanden</p>
                  <p className="text-sm mt-1">Klicken Sie oben, um Bilder hinzuzufügen</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-white border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Status ändern</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant={task.status === 'neu' ? 'default' : 'outline'}
                  onClick={() => handleQuickStatusChange('neu')}
                  className={`rounded-lg ${task.status === 'neu' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  data-testid="status-neu-btn"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Neu
                </Button>
                <Button
                  variant={task.status === 'in_bearbeitung' ? 'default' : 'outline'}
                  onClick={() => handleQuickStatusChange('in_bearbeitung')}
                  className={`rounded-lg ${task.status === 'in_bearbeitung' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                  data-testid="status-progress-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  In Bearbeitung
                </Button>
                <Button
                  variant={task.status === 'erledigt' ? 'default' : 'outline'}
                  onClick={() => handleQuickStatusChange('erledigt')}
                  className={`rounded-lg ${task.status === 'erledigt' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                  data-testid="status-done-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Erledigt
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* History */}
          <Card className="bg-white border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <History className="w-5 h-5 text-slate-600" />
                Verlauf
              </CardTitle>
            </CardHeader>
            <CardContent>
              {task.history && task.history.length > 0 ? (
                <div className="history-timeline">
                  {task.history.slice().reverse().map((entry, index) => (
                    <div key={index} className="history-item">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{entry.action}</p>
                          {entry.details && (
                            <p className="text-sm text-slate-500 mt-0.5">{entry.details}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            {entry.user} • {formatDate(entry.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">Keine Verlaufsdaten</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Property Info */}
          <Card className="bg-white border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <Home className="w-5 h-5 text-slate-600" />
                Objekt
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Link
                to={`/properties/${task.property_id}`}
                className="text-[#0E4C92] font-medium hover:underline flex items-center gap-1"
              >
                {task.property_name}
                <ExternalLink className="w-3 h-3" />
              </Link>
              {task.owner_name && (
                <p className="text-sm text-slate-500 mt-1">Eigentümer: {task.owner_name}</p>
              )}
            </CardContent>
          </Card>

          {/* Assignment */}
          <Card className="bg-white border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <User className="w-5 h-5 text-slate-600" />
                Zugewiesen an
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-900">
                {task.assigned_to_name || <span className="text-slate-400">Nicht zugewiesen</span>}
              </p>
            </CardContent>
          </Card>

          {/* Reporter Info */}
          {(task.reporter_name || task.reporter_email) && (
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope']">Gemeldet von</CardTitle>
              </CardHeader>
              <CardContent>
                {task.reporter_name && (
                  <p className="text-slate-900 font-medium">{task.reporter_name}</p>
                )}
                {task.reporter_email && (
                  <p className="text-slate-500 text-sm">{task.reporter_email}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* E-Mail Status */}
          <Card className="bg-white border border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Mail className="w-5 h-5 text-slate-600" />
                  E-Mail-Status
                </CardTitle>
                {/* Sync-Button anzeigen wenn noch kein Thread oder E-Mail versendet */}
                {(!task.email_thread || task.email_thread.length === 0) && task.email_status === 'versendet' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncEmails}
                    disabled={syncingEmails}
                    className="flex items-center gap-2"
                    data-testid="sync-emails-status-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncingEmails ? 'animate-spin' : ''}`} />
                    {syncingEmails ? 'Sync...' : 'Antworten abrufen'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {task.email_status === 'versendet' ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Versendet
                    </Badge>
                  ) : task.email_status === 'fehler' ? (
                    <Badge className="bg-red-100 text-red-700 border-0">Fehler</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-500 border-0">Entwurf</Badge>
                  )}
                </div>
                {task.email_sent_at && (
                  <p className="text-xs text-slate-400">
                    Gesendet: {formatDate(task.email_sent_at)}
                  </p>
                )}

                {/* Externer Empfänger Badge */}
                {task.notified_external ? (
                  <div
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200"
                    data-testid="notified-external-badge"
                  >
                    <CheckCircle className="w-3 h-3" />
                    {task.notified_external_type === 'owner' && 'Eigentümer informiert'}
                    {task.notified_external_type === 'cleaning' && 'Reinigungsfirma informiert'}
                    {task.notified_external_type === 'contractor' && 'Handwerker informiert'}
                    {!task.notified_external_type && 'Extern informiert'}
                  </div>
                ) : (
                  <div
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200"
                    data-testid="not-notified-external-badge"
                  >
                    Extern noch nicht informiert
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* E-Mail Thread */}
          {task.email_thread && task.email_thread.length > 0 && (
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                    <Mail className="w-5 h-5 text-slate-600" />
                    E-Mail Verlauf ({task.email_thread.length})
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncEmails}
                    disabled={syncingEmails}
                    className="flex items-center gap-2"
                    data-testid="sync-emails-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncingEmails ? 'animate-spin' : ''}`} />
                    {syncingEmails ? 'Sync...' : 'E-Mails abrufen'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {task.email_thread.slice().reverse().map((email, index) => (
                    <div 
                      key={index} 
                      onClick={() => handleOpenEmail(email)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                        email.direction === 'incoming' 
                          ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' 
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                      }`}
                      data-testid={`email-item-${index}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                email.direction === 'incoming' 
                                  ? 'border-blue-300 text-blue-700' 
                                  : 'border-slate-300 text-slate-600'
                              }`}
                            >
                              {email.direction === 'incoming' ? '📥 Eingehend' : '📤 Ausgehend'}
                            </Badge>
                            <span className="text-xs text-slate-400">Klicken zum Öffnen</span>
                          </div>
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {email.direction === 'incoming' 
                              ? `Von: ${email.from_name || email.from_email}` 
                              : `An: ${email.to_email}`}
                          </p>
                          <p className="text-sm text-slate-600 truncate">{email.subject}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {formatDate(email.received_at)}
                          </p>
                          {email.attachments && email.attachments.length > 0 && (
                            <p className="text-xs text-slate-500 mt-1">
                              📎 {email.attachments.length} Anhang/Anhänge
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timestamps */}
          <Card className="bg-white border border-slate-200">
            <CardContent className="pt-6">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Erstellt</span>
                  <span className="text-slate-900">{formatDate(task.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Aktualisiert</span>
                  <span className="text-slate-900">{formatDate(task.updated_at)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) {
          setSelectedTextTags([]); // Reset Tags bei Dialog-Schließung
          setRawNotes(''); // Reset Notizen bei Dialog-Schließung
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Aufgabe bearbeiten</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Titel</Label>
              <Input
                id="edit-title"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-slate-50"
                data-testid="edit-task-title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Beschreibung</Label>
              <Textarea
                id="edit-description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-slate-50 min-h-[100px]"
                data-testid="edit-task-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kategorie</Label>
                <Select
                  value={formData.category || 'reparatur'}
                  onValueChange={(value) => {
                    setFormData({ ...formData, category: value });
                    loadCategoryTags(value);
                    setSelectedTextTags([]); // Reset ausgewählte Tags bei Kategorie-Wechsel
                  }}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="edit-task-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eigentuemer">Eigentümer</SelectItem>
                    <SelectItem value="reinigung">Reinigung</SelectItem>
                    <SelectItem value="reparatur">Reparatur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priorität</Label>
                <Select
                  value={formData.priority || 'normal'}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="edit-task-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="hoch">Hoch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status || 'neu'}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="edit-task-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neu">Neu</SelectItem>
                    <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
                    <SelectItem value="erledigt">Erledigt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Zugewiesen an</Label>
                <Select
                  value={formData.assigned_to || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, assigned_to: value === 'none' ? null : value })}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="edit-task-assigned">
                    <SelectValue placeholder="Nicht zugewiesen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nicht zugewiesen</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Text-Generierung aus Tags + Notizen */}
            <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="flex items-center gap-2 font-semibold text-[#0E4C92]">
                <Tag className="w-4 h-4" />
                Text aus Tags + Notizen generieren
              </Label>
              
              {/* Tag-Auswahl */}
              {categoryTags.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600 font-medium">Betroffene Bereiche (Tags):</p>
                  <div className="flex flex-wrap gap-2" data-testid="text-generation-tags">
                    {categoryTags.map((tag) => {
                      const isSelected = selectedTextTags.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => handleTextTagToggle(tag.id)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                            isSelected 
                              ? 'text-white border-transparent' 
                              : 'bg-white border-slate-300 hover:border-[#0E4C92]'
                          }`}
                          style={{ 
                            backgroundColor: isSelected ? tag.color : undefined
                          }}
                          data-testid={`text-tag-${tag.id}`}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Notizen (Stichsätze) */}
              <div className="space-y-2">
                <Label htmlFor="raw-notes" className="text-sm text-slate-600 font-medium">
                  Notizen (Stichsätze):
                </Label>
                <Textarea
                  id="raw-notes"
                  value={rawNotes}
                  onChange={(e) => setRawNotes(e.target.value)}
                  placeholder="- Toilettendeckel locker&#10;- Spiegelbeleuchtung im Bad funktioniert nicht&#10;- Fenster im Wohnzimmer schließt nicht richtig&#10;- Sofa stark verschmutzt"
                  className="bg-white min-h-[100px] text-sm"
                  data-testid="raw-notes-input"
                />
                <p className="text-xs text-slate-500">
                  Erfassen Sie Punkte als Stichsätze (eine Zeile pro Punkt). Diese werden automatisch in vollständige Sätze umgewandelt.
                </p>
              </div>
              
              <Button
                type="button"
                onClick={handleGenerateText}
                disabled={generatingText || !canGenerateText}
                className="bg-[#0E4C92] hover:bg-[#0A3A70] text-white rounded-lg w-full"
                data-testid="generate-text-btn"
              >
                {generatingText ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Tag className="w-4 h-4 mr-2" />
                )}
                Text aus Tags + Notizen generieren
              </Button>
              
              {!canGenerateText && (
                <p className="text-xs text-amber-600 text-center">
                  Bitte wählen Sie mindestens einen Tag aus oder geben Sie Notizen ein
                </p>
              )}
            </div>

            {/* Tags Selection */}
            {availableTags.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Tags
                </Label>
                <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg min-h-[60px]" data-testid="edit-task-tags">
                  {availableTags.map((tag) => {
                    const isSelected = (formData.tags || []).includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => {
                          const currentTags = formData.tags || [];
                          const newTags = isSelected
                            ? currentTags.filter(t => t !== tag.id)
                            : [...currentTags, tag.id];
                          setFormData({ ...formData, tags: newTags });
                        }}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                          isSelected 
                            ? 'text-white ring-2 ring-offset-1' 
                            : 'opacity-50 hover:opacity-75'
                        }`}
                        style={{ 
                          backgroundColor: tag.color,
                          ringColor: isSelected ? tag.color : 'transparent'
                        }}
                        data-testid={`tag-select-${tag.id}`}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-stone-500">Klicken Sie auf Tags, um sie hinzuzufügen oder zu entfernen</p>
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                className="flex-1 rounded-lg"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-lg"
                disabled={submitting}
                data-testid="save-task-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Speichern
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <Mail className="w-5 h-5" />
              E-Mail versenden
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendEmail} className="space-y-4 mt-4">
            {/* Ticket Info */}
            {task.ticket_id && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Ticket-ID:</strong> {task.ticket_id}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  <strong>Betreff:</strong> [{task.ticket_id}] {task.property_name} – {emailData.subject_suffix}
                </p>
                <p className="text-xs text-blue-600">
                  <strong>Reply-To:</strong> info+{task.ticket_id}@gb-sylt.de
                </p>
              </div>
            )}
            
            {/* Empfänger – neue Logik: To=info immer, CC=extern optional */}
            <div className="space-y-3">
              <Label className="font-semibold">1. Empfänger</Label>

              {/* To: info@gb-sylt.de (fest, nicht editierbar) */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">An: info@gb-sylt.de</p>
                  <p className="text-xs text-blue-500">Primärer Empfänger (nicht änderbar)</p>
                </div>
              </div>

              {/* CC: Externer Empfänger (optional) */}
              {loadingRecipients ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Lade Empfänger...
                </div>
              ) : recipients.length > 0 ? (
                <label
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    ccExternal ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="cc-external-label"
                >
                  <input
                    type="checkbox"
                    checked={ccExternal}
                    onChange={(e) => setCcExternal(e.target.checked)}
                    className="mt-0.5 rounded text-emerald-600 w-4 h-4"
                    data-testid="cc-external-checkbox"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">
                      Kopie an {recipients[0]?.name || recipients[0]?.email}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{recipients[0]?.email} ({recipients[0]?.subtitle || recipients[0]?.type})</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {ccExternal ? '✓ Wird als CC hinzugefügt' : 'Haken setzen für CC-Kopie'}
                    </p>
                  </div>
                </label>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg opacity-60">
                  <input type="checkbox" disabled className="w-4 h-4" />
                  <div>
                    <p className="text-sm text-slate-500">Kein externer Empfänger verfügbar</p>
                    <p className="text-xs text-slate-400">
                      Für Kategorie „{getCategoryLabel(task.category)}" kein Kontakt hinterlegt
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Tags Selection */}
            <div className="space-y-2">
              <Label className="font-semibold">2. Hinweise auswählen (optional)</Label>
              <div className="flex flex-wrap gap-2">
                {categoryTags.length > 0 ? categoryTags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleTagToggle(tag.id)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      emailData.selectedTags.includes(tag.id)
                        ? 'bg-[#0E4C92] text-white border-[#0E4C92]'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-[#0E4C92]'
                    }`}
                    data-testid={`email-tag-${tag.id}`}
                  >
                    {tag.name}
                  </button>
                )) : (
                  <p className="text-sm text-slate-500 italic">Keine Tags für diese Kategorie verfügbar</p>
                )}
              </div>
              {emailData.selectedTags.length > 0 && (
                <p className="text-xs text-emerald-600">
                  ✓ {emailData.selectedTags.length} Hinweis(e) ausgewählt
                </p>
              )}
            </div>
            
            {/* Free Text */}
            <div className="space-y-2">
              <Label htmlFor="email-freetext" className="font-semibold">3. Zusätzliche Details (optional)</Label>
              <Textarea
                id="email-freetext"
                value={emailData.freeText}
                onChange={(e) => handleFreeTextChange(e.target.value)}
                placeholder="Hier können Sie zusätzliche Informationen eingeben..."
                className="bg-slate-50 min-h-[80px]"
                data-testid="email-freetext"
              />
            </div>
            
            {/* Preview / Edit Body */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="email-body" className="font-semibold">4. E-Mail-Entwurf (editierbar)</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => updateEmailBody(emailData.selectedTags, emailData.freeText)}
                  className="text-xs text-[#0E4C92]"
                >
                  Vorlage neu laden
                </Button>
              </div>
              <Textarea
                id="email-body"
                value={emailData.body}
                onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                className="bg-slate-50 min-h-[200px] font-mono text-xs"
                data-testid="email-body"
              />
            </div>
            
            {/* Attachments */}
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="include-images"
                checked={emailData.include_images}
                onChange={(e) => setEmailData({ ...emailData, include_images: e.target.checked })}
                className="rounded text-[#0E4C92]"
                data-testid="email-include-images"
              />
              <Label htmlFor="include-images" className="text-sm cursor-pointer">
                📎 Alle Ticket-Fotos als Anhang senden ({task.images?.length || 0} Foto{(task.images?.length || 0) !== 1 ? 's' : ''})
              </Label>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEmailDialogOpen(false)}
                className="flex-1 rounded-lg"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-lg"
                disabled={sendingEmail}
                data-testid="send-email-submit"
              >
                {sendingEmail ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Senden
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Bildvorschau</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Vorschau"
              className="w-full h-auto max-h-[70vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aufgabe löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diese Aufgabe wirklich löschen? Alle zugehörigen Bilder werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 rounded-lg"
              data-testid="confirm-delete-btn"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Overwrite Dialog */}
      <AlertDialog open={confirmOverwriteOpen} onOpenChange={setConfirmOverwriteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vorhandenen Text überschreiben?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Beschreibung enthält bereits Text. Möchten Sie diesen mit dem generierten Text überschreiben?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeTextGeneration}
              className="bg-[#0E4C92] hover:bg-[#0A3A70] rounded-lg"
              data-testid="confirm-overwrite-btn"
            >
              Überschreiben
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* E-Mail Ansicht Dialog */}
      <Dialog open={emailViewDialogOpen} onOpenChange={setEmailViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEmail?.direction === 'incoming' ? (
                <Badge className="bg-blue-100 text-blue-700 border-0">📥 Eingehend</Badge>
              ) : (
                <Badge className="bg-slate-100 text-slate-600 border-0">📤 Ausgehend</Badge>
              )}
              <span className="truncate">{selectedEmail?.subject || 'E-Mail'}</span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedEmail && (
            <div className="flex-1 overflow-auto space-y-4">
              {/* E-Mail Header */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-2 text-sm">
                <div className="flex">
                  <span className="text-slate-500 w-20 flex-shrink-0">Von:</span>
                  <span className="text-slate-900 font-medium">
                    {selectedEmail.from_name ? `${selectedEmail.from_name} <${selectedEmail.from_email}>` : selectedEmail.from_email}
                  </span>
                </div>
                <div className="flex">
                  <span className="text-slate-500 w-20 flex-shrink-0">An:</span>
                  <span className="text-slate-900">{selectedEmail.to_email}</span>
                </div>
                <div className="flex">
                  <span className="text-slate-500 w-20 flex-shrink-0">Datum:</span>
                  <span className="text-slate-900">{formatDate(selectedEmail.received_at)}</span>
                </div>
                <div className="flex">
                  <span className="text-slate-500 w-20 flex-shrink-0">Betreff:</span>
                  <span className="text-slate-900 font-medium">{selectedEmail.subject}</span>
                </div>
                {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                  <div className="flex">
                    <span className="text-slate-500 w-20 flex-shrink-0">Anhänge:</span>
                    <span className="text-slate-900">📎 {selectedEmail.attachments.length} Datei(en)</span>
                  </div>
                )}
              </div>

              {/* E-Mail Body */}
              <div className="border rounded-lg p-4 bg-white min-h-[200px]">
                {selectedEmail.body_html ? (
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }}
                  />
                ) : selectedEmail.body_preview ? (
                  <div className="whitespace-pre-wrap text-sm text-slate-700">
                    {selectedEmail.body_preview}
                  </div>
                ) : (
                  <p className="text-slate-400 italic">Kein Inhalt verfügbar</p>
                )}
              </div>

              {/* Anhänge */}
              {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <p className="text-sm font-medium text-slate-700 mb-2">Anhänge:</p>
                  <div className="space-y-2">
                    {selectedEmail.attachments.map((att, idx) => (
                      <a
                        key={idx}
                        href={`${BACKEND_URL}${att.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        📎 {att.name || `Anhang ${idx + 1}`}
                        {att.size && <span className="text-slate-400">({Math.round(att.size / 1024)} KB)</span>}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setEmailViewDialogOpen(false)}
              className="rounded-lg"
            >
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskDetailPage;
